import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

const shortcuts = [
  { key: "?", description: "Show keyboard shortcuts" },
  { key: "g d", description: "Go to Dashboard" },
  { key: "g p", description: "Go to Properties" },
  { key: "g s", description: "Go to STATEC" },
  { key: "g h", description: "Go to Profile" },
  { key: "/", description: "Focus search (if available)" },
  { key: "Esc", description: "Close modals/dialogs" },
];

export const KeyboardShortcutsOverlay = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [gPressed, setGPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip keyboard shortcuts when focus is on an input, textarea, or contenteditable
      const target = e.target as HTMLElement;
      const isTextInput = target.tagName === "INPUT" || 
                          target.tagName === "TEXTAREA" || 
                          target.isContentEditable;

      // Show shortcuts overlay (only outside text inputs)
      if (e.key === "?" && !e.shiftKey && !isTextInput) {
        e.preventDefault();
        setOpen(true);
        return;
      }

      // Handle 'g' prefix for navigation (only outside text inputs)
      if (e.key === "g" && !e.ctrlKey && !e.metaKey && !isTextInput) {
        e.preventDefault();
        setGPressed(true);
        setTimeout(() => setGPressed(false), 1000);
        return;
      }

      // Navigation shortcuts
      if (gPressed) {
        e.preventDefault();
        setGPressed(false);
        switch (e.key) {
          case "d":
            navigate("/dashboard");
            break;
          case "p":
            navigate("/properties");
            break;
          case "s":
            navigate("/statec");
            break;
          case "h":
            navigate("/profile");
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, gPressed]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Use these shortcuts to navigate quickly through the application
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-4">
          {shortcuts.map((shortcut) => (
            <div
              key={shortcut.key}
              className="flex items-center justify-between gap-4"
            >
              <span className="text-sm text-muted-foreground">
                {shortcut.description}
              </span>
              <kbd className="pointer-events-none inline-flex h-7 select-none items-center gap-1 rounded border bg-muted px-2 font-mono text-xs font-medium text-muted-foreground">
                {shortcut.key}
              </kbd>
            </div>
          ))}
        </div>
        <div className="text-xs text-muted-foreground border-t pt-4">
          <p>Press <kbd className="px-1.5 py-0.5 rounded bg-muted">Esc</kbd> to close this dialog</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
